import { SALE_TRANSACTION_FINISHED } from "../../../_Bootstrap/GlobalConstants";
import { store } from "../../../_Bootstrap/RoutingAndStore";
import BaseAgent, { generateWorkerURL } from "../../../Workers/Bases/BaseAgent";
import { getCurrencyDecimalPlaces } from "../../Generic/Numbers";
import { getLogLevel, LogLevel } from "../../Platform/Logging";
import { getOption, subscribeToOption } from "../../Platform/Options/Options";
import { PROMOTION_EVENTS } from "./PromotionEvents";
import { PromotionWorkerHandler } from "./PromotionWorkerHandler";

declare global {
    interface DocumentEventMap {
        productModified: CustomEvent;
    }
}

export class PromotionAgentCommunicator extends BaseAgent {
    protected ready: boolean;
    protected readyQueue: Array<() => void>;
    protected handler: PromotionWorkerHandler;
    protected useWorker = true;
    protected workerRunning = false;

    constructor() {
        super(generateWorkerURL("promotionworker"), "Shopfront Promotions", "dedicated", true, false);

        this.updateSetting = this.updateSetting.bind(this);

        this.ready = false;
        this.readyQueue = [];

        this.prepareSettingsUpdate();
        this.handler = new PromotionWorkerHandler(this.onPromotionWorkerReady);

        subscribeToOption("outlet_loyalty_rate", this.updateSetting);
        subscribeToOption("outlet_redemption_rate", this.updateSetting);
        subscribeToOption("use_quantity_rate", this.updateSetting);
        subscribeToOption("cross_promotion_count", this.updateSetting);
        subscribeToOption("rounding_mode", this.updateSetting);
        subscribeToOption(
            "family_distribution",
            this.updateSetting
        );

        let useWorker = subscribeToOption("register_dedicated_promotion_worker", this.useDedicatedWorker);

        if(process.env.NODE_ENV === "test") {
            // Force tests to disable the worker
            useWorker = false;
        }

        this.useDedicatedWorker("register_dedicated_promotion_worker", useWorker);

        // Listen for transaction end events
        if(typeof document !== "undefined") {
            document.addEventListener(SALE_TRANSACTION_FINISHED, this.handleTransactionFinished);
            document.addEventListener("synchronisationFinished", this.syncFinished);
            document.addEventListener("productModified", this.syncFinished);
        }
    }

    protected prepareSettingsUpdate(): void {
        this.readyQueue.push(() => {
            const currentState = store.getState();

            this.updateSetting("outlet_loyalty_rate", getOption("outlet_loyalty_rate"));
            this.updateSetting("outlet_redemption_rate", getOption("outlet_redemption_rate"));
            this.updateSetting("use_quantity_rate", getOption("use_quantity_rate"));
            this.updateSetting("cross_promotion_count", getOption("cross_promotion_count"));
            this.updateSetting("family_distribution", getOption("family_distribution"));
            this.updateSetting("decimal_places", getCurrencyDecimalPlaces());
            this.updateSetting("rounding_mode", getOption("rounding_mode"));

            if(currentState.sellscreen.sale) {
                this.updateSetting(
                    "price_set",
                    currentState.sellscreen.sale.priceSet === "" ? false : currentState.sellscreen.sale.priceSet
                );
            }
        });
    }

    protected useDedicatedWorker = (_option: "register_dedicated_promotion_worker", newValue: boolean): void => {
        this.useWorker = newValue;

        if(this.workerRunning && this.useWorker || !this.workerRunning && !this.useWorker) {
            return;
        }

        this.prepareSettingsUpdate();
        this.terminateWorker();

        if(this.useWorker) {
            this.spawnWorker()
                .then(() => {
                    this.startWorker();
                    this.workerRunning = true;
                });
        }
    };

    protected terminateWorker(): void {
        super.terminateWorker();
        this.workerRunning = false;
    }

    protected handleTransactionFinished = (): void => {
        void this.sendMessage(PROMOTION_EVENTS.TRANSACTION_FINISHED);
    };

    protected syncFinished = (event: CustomEvent): void => {
        if(
            typeof event.detail?.updated !== "undefined" &&
            !event.detail.updated.promotions &&
            !event.detail.updated.products
        ) {
            return;
        }

        void this.sendMessage(PROMOTION_EVENTS.SYNC_FINISHED);
    };

    public updateSetting(option: string, newValue: string | number | boolean): void {
        let setting: string | null = null;

        if(option === "outlet_loyalty_rate") {
            setting = "earn_rate";
        } else if(option === "outlet_redemption_rate") {
            setting = "redemption_rate";
        } else {
            switch(option) {
                case "use_quantity_rate":
                case "cross_promotion_count":
                case "family_distribution":
                case "price_set":
                case "timeout_enabled":
                case "decimal_places":
                case "rounding_mode":
                    setting = option;
                    break;
            }
        }

        if(setting === null) {
            throw new Error("Unknown setting provided to promotion agent");
        }

        void this.sendMessage(PROMOTION_EVENTS.UPDATE_SETTINGS, {
            setting,
            value: newValue,
        }, null, true);
    }

    /**
     * Handle messages received from the PromotionWorker
     * @param {{type: PROMOTION_EVENTS, message: any}} data
     * @protected
     */
    protected handleMessage(data: {
        type: PROMOTION_EVENTS;
        message: any;
    }): void {
        if(!data.type) {
            console.error("Unknown data passed to PromotionAgent", data);

            return;
        }

        switch(data.type) {
            case PROMOTION_EVENTS.READY:
                this.onPromotionWorkerReady();
                break;
        }
    }

    protected onPromotionWorkerReady = (): void => {
        this.ready = true;

        for(let i = 0, l = this.readyQueue.length; i < l; i++) {
            this.readyQueue[i]();
        }

        this.readyQueue = [];
    };

    public async sendMessage(
        event: PROMOTION_EVENTS,
        message: any = null,
        success: ((...args: Array<any>) => any) | null = null,
        toBoth = false
    ): Promise<void> {
        if(!this.ready) {
            const id = Date.now();

            if(process.env.NODE_ENV !== "test" && getLogLevel() >= LogLevel.NORMAL) {
                console.info(`Promotion communicator is not ready yet, adding message to ready queue ${id}`, message);
            }

            await new Promise<void>(res => {
                this.readyQueue.push(res);
            });

            if(process.env.NODE_ENV !== "test" && getLogLevel() >= LogLevel.NORMAL) {
                console.info(`Promotion communicator received ready signal, sending message ${id}`);
            }
        }

        const sendToWorker = this.useWorker;
        const sendToLocal = !this.useWorker || toBoth;

        if(sendToWorker) {
            super.sendMessage(event, message, success);
        }

        if(sendToLocal) {
            this.handler.handle({
                type: event,
                message,
            }, success);
        }
    }

    /**
     * Ensure the promotion calculation is ready (used only in testing)
     */
    public waitForReady(): Promise<void> {
        if(this.ready) {
            return Promise.resolve();
        }

        return new Promise(res => {
            this.readyQueue.push(res);
        });
    }
}
